import type { TFunction } from "i18next";
import { itemIdOf } from "../../../api/content/processDefinitions";
import type { DataTypeRef } from "../../../api/content/workflowsApi";
import { typesText } from "../inputCards/typeWords";
import type { PaletteItem } from "./paletteDrag";
import type { PickerItems, ProposedIn } from "./pickerItems";

/**
 * The step picker's entries (AU-6, F36), grouped by where they come from: the project's
 * own steps first, then each library it pins, then the Workflows it may place as
 * sub-workflows. Each entry says what it takes and gives, with types, so a step that fits
 * can be found by its name or by a type. Pure.
 */
export type PickerSource = { readonly kind: "own" } | { readonly kind: "library"; readonly projectId: string } | { readonly kind: "workflows" };

export interface PickerPort {
  readonly name: string;
  readonly types: readonly DataTypeRef[];
  /** For an input: whether the step needs it; `null` for an output. */
  readonly need: "required" | "optional" | null;
}

export interface PickerEntry {
  /** What a drop or an Add button places. */
  readonly item: PaletteItem;
  readonly stepKind: "agent" | "human" | "tool" | null;
  readonly description: string;
  readonly takes: readonly PickerPort[];
  readonly gives: readonly PickerPort[];
  /** For a Workflow: how many steps it has. */
  readonly stepCount: number | null;
  readonly proposedIn: ProposedIn | null;
  /** What module B says it is found by, in every language it has (P-21). */
  readonly searchText: string | null;
}

export interface PickerGroup {
  readonly source: PickerSource;
  readonly entries: readonly PickerEntry[];
}

/** The groups, each sorted by name with proposed versions after; the edited Workflow is left out. */
export function pickerGroups(items: PickerItems, selfItemId: string): readonly PickerGroup[] {
  const own: PickerEntry[] = [];
  const libraries = new Map<string, PickerEntry[]>();
  for (const step of items.steps) {
    const entry: PickerEntry = {
      item: { kind: "step", reference: step.reference, name: step.name },
      stepKind: step.stepKind,
      description: step.description,
      takes: step.inputs.map((input) => ({ name: input.label === "" ? input.name : input.label, types: input.accepts, need: input.required ? "required" : "optional" })),
      gives: step.outputs.map((output) => ({ name: output.name, types: output.produces, need: null })),
      stepCount: null,
      proposedIn: step.proposedIn,
      searchText: step.searchText,
    };
    if (step.library === null) {
      own.push(entry);
    } else {
      libraries.set(step.library, [...(libraries.get(step.library) ?? []), entry]);
    }
  }
  const workflows = items.workflows
    .filter((workflow) => itemIdOf(workflow.reference) !== selfItemId)
    .map((workflow): PickerEntry => ({ item: { kind: "workflow", reference: workflow.reference, name: workflow.name }, stepKind: null, description: workflow.description, takes: [], gives: [], stepCount: workflow.stepCount, proposedIn: workflow.proposedIn, searchText: workflow.searchText }));
  const groups: PickerGroup[] = [
    { source: { kind: "own" }, entries: sorted(own) },
    ...[...libraries.entries()].sort(([left], [right]) => left.localeCompare(right)).map(([projectId, entries]): PickerGroup => ({ source: { kind: "library", projectId }, entries: sorted(entries) })),
    { source: { kind: "workflows" }, entries: sorted(workflows) },
  ];
  return groups.filter((group) => group.entries.length > 0);
}

/**
 * The groups with only the entries that match every word of `query`: a Workflow in what
 * module B says it is found by (`searchText`, every language it has, P-21); a step in its
 * name, what it does, a port's name or a port's type in words ("annotations" finds the steps
 * that take or give Annotations).
 */
export function searchPicker(groups: readonly PickerGroup[], query: string, t: TFunction): readonly PickerGroup[] {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter((word) => word !== "");
  if (words.length === 0) {
    return groups;
  }
  const text = (entry: PickerEntry) =>
    [entry.searchText ?? `${entry.item.name} ${entry.description}`, ...[...entry.takes, ...entry.gives].flatMap((port) => [port.name, typesText(port.types, t)])].join(" ").toLocaleLowerCase();
  return groups.map((group) => ({ ...group, entries: group.entries.filter((entry) => words.every((word) => text(entry).includes(word))) })).filter((group) => group.entries.length > 0);
}

function sorted(entries: readonly PickerEntry[]): PickerEntry[] {
  return [...entries].sort((left, right) => left.item.name.localeCompare(right.item.name) || Number(left.proposedIn !== null) - Number(right.proposedIn !== null));
}
